import { useCallback, useEffect, useRef } from "react";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectActiveToolCall,
  selectIsStreaming,
  selectSendMessageStatus,
  selectStreamingResponse,
} from "app/containers/Chatbot/slice";

// Keeps a chat scroll container pinned to the newest message. Returns the ref
// to put on the scrolling element.
//
// The message input is sticky inside the scroll container, so scrolling an
// end-of-list sentinel into view can leave the latest message hidden behind
// it. Scrolling the container to its own scrollHeight always clears the input.
export const useScrollToLatestMessage = (messageCount: number) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sendMessageStatus = useAppSelector(selectSendMessageStatus);
  const streamingResponse = useAppSelector(selectStreamingResponse);
  const isStreaming = useAppSelector(selectIsStreaming);
  const activeToolCall = useAppSelector(selectActiveToolCall);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const element = scrollContainerRef.current;
    if (!element) return;
    element.scrollTo({ top: element.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    if (messageCount > 0) {
      scrollToBottom();
    }
  }, [messageCount, scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
  }, [sendMessageStatus, activeToolCall, scrollToBottom]);

  // Streamed chunks arrive faster than a smooth scroll can settle, so follow
  // the stream instantly to avoid queueing animations against each other.
  useEffect(() => {
    if (isStreaming && streamingResponse) {
      scrollToBottom("auto");
    }
  }, [isStreaming, streamingResponse, scrollToBottom]);

  return scrollContainerRef;
};
